import HOOK from "../../components/hooks"
import { BASE, CATEGORY, COLOR, COLUMN, FORMATTING, JARGON, LAYOUT, PATH } from "../../modules/constants"
import { CHECKBOX } from "../../modules/checkbox_constants"
import { addPaddingRightToLastCell, calcStats, getCellWithTooltip, headCell } from "../../modules/table.funcs"
import { formatInteger, formatDate_toLocal, getCheckboxName } from "../../modules/utilities"

const { bold, border1, center, left, string } = FORMATTING
const borderLeft = border1

// Creates the home page LSE Companies table (headers and rows) from lse.companiesMeta - the 'items'

//#region Header
export const createLseNewsHeadersData = props => {
    // console.log('createCompaniesHeadersData props:', props)
    const { companyPaddingleft, nexus, portrait, showLogos, the350club } = props

    const dateHead = headCell({
        id: COLUMN.PUBLICATION_DATE,
        align: center,
        borderLeft: portrait ? null : borderLeft,
        paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        paddingRight: LAYOUT.HOME.PADDING.BORDER,
        width: 100,
    })

    const companyHead = headCell({
        id: COLUMN.COMPANY,
        paddingLeft: companyPaddingleft,
        align: left,
    })

    const typeHead = headCell({
        id: COLUMN.TYPE,
        align: left,
    })

    const headlineHead = headCell({
        align: left,
        id: COLUMN.HEADLINE,
    })

    // 5px extra gap to Summary, on top of the default paddingRight
    const lseHeadlineHead = { ...headlineHead, paddingRight: LAYOUT.HOME.PADDING.RIGHT + 5 }

    const epicHead = headCell({
        align: left,
        id: COLUMN.EPIC,
        width: 50,
    })

    const indexHead = headCell({
        align: left,
        id: COLUMN.LSE_INDEX,
        width: 120,
    })

    const aiSummaryHead = headCell({
        align: left,
        id: COLUMN.AI_SUMMARY,
    })

    const logoHead = headCell({
        align: left,
        id: COLUMN.LOGO,
        width: LAYOUT.LOGO_WIDTH,
    })

    const starHead = headCell({
        align: left,
        id: COLUMN.STAR,
        width: LAYOUT.STAR_WIDTH,
    })

    const headCells = nexus
        ? props.hubData.lsePerson // nexus - Company/Person News view
            ? portrait
                ? [companyHead, dateHead, headlineHead] // person portrait
                : [companyHead, dateHead, typeHead, headlineHead, aiSummaryHead] // person landscape
            : portrait
                ? [dateHead, headlineHead, aiSummaryHead] // company portrait
                : [dateHead, typeHead, headlineHead, aiSummaryHead] // company landscape
        : portrait // LSE News tab
            ? [starHead, dateHead, companyHead, lseHeadlineHead]
            : [starHead, companyHead, epicHead, indexHead, dateHead, lseHeadlineHead, aiSummaryHead]

    if (showLogos && (!portrait || nexus))
        headCells.splice(nexus ? 0 : 1, 0, logoHead) // insert logo header (no fav star if nexus)

    if (the350club)
        headCells.shift() // Remove starHead

    addPaddingRightToLastCell(headCells)
    return headCells
}
//#endregion Header

//#region Footers
export const createLseNewsFootersData = props => {
    // console.log('createLseNewsFooterwData props:', props)
    // NB: No footers for nexus company or person news
    const { portrait, showLogos, stats, the350club } = props
    const label = ""

    for (const property in stats)
        calcStats(stats[property])

    const starFoot = headCell({ id: COLUMN.STAR, label })
    const logoFoot = headCell({ id: COLUMN.LOGO, label })
    const nameFoot = headCell({ id: COLUMN.NAME, label })
    const epicFoot = headCell({ id: COLUMN.EPIC, label })
    const indexFoot = headCell({ id: COLUMN.LSE_INDEX, label })
    const publicationDateFoot = headCell({
        borderLeft: portrait ? null : borderLeft,
        id: COLUMN.PUBLICATION_DATE,
        label,
        paddingLeft: LAYOUT.HOME.PADDING.BORDER,
    })
    const numReleasesLabel = `${formatInteger(stats.numRows)} ${stats.numRows === 1 ? JARGON.NEWS_RELEASE : JARGON.NEWS_RELEASE + "s"}`
    const aiSummaryFoot = headCell({ id: COLUMN.AI_SUMMARY, align: left, label: numReleasesLabel })

    const headlineFoot = headCell({ id: COLUMN.HEADLINE, label })

    // In portrait there's no AI Summary column, so the headline footer carries the release count instead
    const portraitHeadlineFoot = { ...headlineFoot, label: numReleasesLabel }

    const typeFoot = headCell({ id: COLUMN.TYPE, label })

    const footCells = {
        totals: portrait
            ? [starFoot, publicationDateFoot, nameFoot, portraitHeadlineFoot]
            : [starFoot, nameFoot, epicFoot, indexFoot, publicationDateFoot, headlineFoot, aiSummaryFoot],
        means: null
    }

    if (showLogos && !portrait)
        footCells.totals.splice(0, 0, logoFoot) // insert logo footer

    if (the350club)
        footCells.totals.shift() // Remove starFoot

    addPaddingRightToLastCell(footCells.totals)
    // addPaddingRightToLastCell(footCells.means)

    return footCells
}
//#endregion Footers

//#region Rows
export const createLseNewsRowData = props => {
    // console.log('createLseNewsRowData props: ', props)
    const { items, stats } = props
    const rows = []

    items.forEach(item => {
        rows.push(createLseNewsRow({ ...props, item }))
        ++stats.numRows
    })
    return rows
}

const createLseNewsRow = ({ companyPaddingleft, hubData, item, nexus, portrait, showLogos, stats }) => {
    // if nexus === true we are on the company news view - as opposed to the LSE News tab

    const publicationDate = getCellWithTooltip({
        align: center,
        bold,
        borderLeft: portrait ? null : borderLeft,
        paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        paddingRight: LAYOUT.HOME.PADDING.BORDER,
        sortValue: item.publicationDate,
        value: formatPublicationDate(item.publicationDate),
    })

    const name = getCellWithTooltip({
        type: string,
        paddingLeft: companyPaddingleft,
        value: item.company,
    })

    const ftseIndex = getCellWithTooltip({
        sortValue: item.indexBit,
        type: string,
        value: getCheckboxName(CHECKBOX[HOOK.LSE_INDEX], item.indexBit),
    })

    const epic = getCellWithTooltip({
        type: string,
        value: item.epics[0],
        tooltip: item.epics.join(', '),
    })

    const type = getCellWithTooltip({
        type: string,
        value: item.typeDescription,
    })

    const headline = getCellWithTooltip({
        type: string,
        value: item.headline,
    })

    // 5px extra gap to Summary, on top of the default paddingRight
    const lseHeadline = { ...headline, paddingRight: LAYOUT.HOME.PADDING.RIGHT + 5 }

    const aiSummary = getCellWithTooltip({
        type: string,
        value: item.summary,
    })

    const logo = showLogos && (!portrait || nexus)
        ? item.logo ?? "no logo"
        : null

    const cellWithTooltips = nexus
        ? hubData.lsePerson
            ? portrait
                ? [name, publicationDate, headline] // Person News portrait view
                : [name, publicationDate, type, headline, aiSummary] // Person News view
            : portrait
                ? [publicationDate, headline, aiSummary] // Company News portrait view
                : [publicationDate, type, headline, aiSummary] // Company News view
        : portrait // LSE News tab
            ? [publicationDate, name, lseHeadline] // portrait
            : [name, epic, ftseIndex, publicationDate, lseHeadline, aiSummary] // landscape

    addPaddingRightToLastCell(cellWithTooltips)

    return {
        _id: item._id,
        category: CATEGORY.NEWS,
        cellWithTooltips,
        iconColor: COLOR.GOOD,
        index: item.index,
        isFavourite: nexus ? null : item.favourite,
        link: `/${PATH[BASE.NEWS_RELEASE]}/${item._id}`,
        logo,
        title: item.companyName,
    }
}

const isToday = testDate =>
    testDate.toDateString() === new Date().toDateString()

const formatPublicationDate = date => {
    const publicationDate = typeof date === "string" ? new Date(date) : date
    return isToday(publicationDate)
        ? `TODAY ${publicationDate.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit' })}`
        : formatDate_toLocal(date)
}
//#endregion Rows

